import { supabase } from '../lib/supabase';

export const budgetService = {
  // Get all budget limits for a user and month (including recurring budgets)
  async getBudgetLimits(userId, monthYear) {
    // Parse the target month
    const [targetYear, targetMonth] = monthYear?.split('-')?.map(Number);
    const targetDate = new Date(targetYear, targetMonth - 1, 1);

    // Fetch all budgets for this specific month OR recurring budgets that apply to this month
    const { data, error } = await supabase?.from('budget_limits')?.select(`
        *,
        categories (
          id,
          name,
          type,
          color,
          icon
        )
      `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    // Filter budgets to include:
    // 1. Non-recurring budgets for this specific month
    // 2. Recurring budgets that apply to this month
    const applicableBudgets = data?.filter(item => {
      // Non-recurring budget - must match exact month
      if (!item?.is_recurring) {
        return item?.month_year === monthYear;
      }

      // Recurring budget - check if target month falls within the recurrence period
      const startDate = new Date(item?.start_date);
      const endDate = item?.end_date ? new Date(item?.end_date) : null;

      // Check if target month is after start date
      if (targetDate < startDate) {
        return false;
      }

      // Check if target month is before end date (if end date exists)
      if (endDate && targetDate > endDate) {
        return false;
      }

      // Check frequency
      const frequency = item?.recurrence_frequency;
      const monthsDiff = (targetYear - startDate?.getFullYear()) * 12 + (targetMonth - 1 - startDate?.getMonth());

      if (frequency === 'weekly') {
        // For weekly budgets, check if target date is within any week that starts from start_date
        const daysDiff = Math.floor((targetDate - startDate) / (1000 * 60 * 60 * 24));
        const weeksDiff = Math.floor(daysDiff / 7);
        // Budget applies if we're in a week that started on or after start_date
        return daysDiff >= 0;
      } else if (frequency === 'biweekly') {
        // For biweekly budgets, check if target date falls in a biweekly period
        const daysDiff = Math.floor((targetDate - startDate) / (1000 * 60 * 60 * 24));
        const biweeksDiff = Math.floor(daysDiff / 14);
        // Budget applies if we're in a biweekly period that started on or after start_date
        return daysDiff >= 0;
      } else if (frequency === 'monthly') {
        return true; // Every month
      } else if (frequency === 'quarterly') {
        return monthsDiff % 3 === 0; // Every 3 months
      } else if (frequency === 'yearly') {
        return monthsDiff % 12 === 0; // Every 12 months
      }

      return false;
    });

    // Convert snake_case to camelCase
    return applicableBudgets?.map(item => ({
      id: item?.id,
      userId: item?.user_id,
      categoryId: item?.category_id,
      monthYear: item?.month_year,
      limitAmount: parseFloat(item?.limit_amount),
      alertThreshold1: item?.alert_threshold_1,
      alertThreshold2: item?.alert_threshold_2,
      rolloverEnabled: item?.rollover_enabled,
      rolloverAmount: parseFloat(item?.rollover_amount),
      isRecurring: item?.is_recurring || false,
      recurrenceFrequency: item?.recurrence_frequency,
      startDate: item?.start_date,
      endDate: item?.end_date,
      createdAt: item?.created_at,
      updatedAt: item?.updated_at,
      category: item?.categories ? {
        id: item?.categories?.id,
        name: item?.categories?.name,
        type: item?.categories?.type,
        color: item?.categories?.color,
        icon: item?.categories?.icon
      } : null
    }));
  },

  // Get budget progress for a user and month
  async getBudgetProgress(userId, monthYear) {
    const { data, error } = await supabase?.rpc('get_budget_progress', {
        p_user_id: userId,
        p_month_year: monthYear
      });

    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(item => ({
      budgetId: item?.budget_id,
      categoryId: item?.category_id,
      categoryName: item?.category_name,
      limitAmount: parseFloat(item?.limit_amount),
      currentSpending: parseFloat(item?.current_spending),
      remainingBudget: parseFloat(item?.remaining_budget),
      percentageUsed: parseFloat(item?.percentage_used),
      alertThreshold1: item?.alert_threshold_1,
      alertThreshold2: item?.alert_threshold_2
    }));
  },

  // Create a new budget limit
  async createBudgetLimit(budgetData) {
    const insertData = {
      user_id: budgetData?.userId,
      category_id: budgetData?.categoryId,
      month_year: budgetData?.monthYear,
      limit_amount: budgetData?.limitAmount,
      alert_threshold_1: budgetData?.alertThreshold1 || 80,
      alert_threshold_2: budgetData?.alertThreshold2 || 95,
      rollover_enabled: budgetData?.rolloverEnabled || false,
      rollover_amount: budgetData?.rolloverAmount || 0,
      is_recurring: budgetData?.isRecurring || false
    };

    // Add recurring fields if this is a recurring budget
    if (budgetData?.isRecurring) {
      insertData.recurrence_frequency = budgetData?.recurrenceFrequency || 'monthly';
      // Use monthYear as start_date if not provided
      insertData.start_date = budgetData?.startDate || `${budgetData?.monthYear}-01`;
      if (budgetData?.endDate) {
        insertData.end_date = budgetData?.endDate;
      }
    } else {
      // For non-recurring budgets, set start_date to the month_year
      insertData.start_date = `${budgetData?.monthYear}-01`;
    }

    const { data, error } = await supabase?.from('budget_limits')?.insert(insertData)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      categoryId: data?.category_id,
      monthYear: data?.month_year,
      limitAmount: parseFloat(data?.limit_amount),
      alertThreshold1: data?.alert_threshold_1,
      alertThreshold2: data?.alert_threshold_2,
      rolloverEnabled: data?.rollover_enabled,
      rolloverAmount: parseFloat(data?.rollover_amount),
      isRecurring: data?.is_recurring || false,
      recurrenceFrequency: data?.recurrence_frequency,
      startDate: data?.start_date,
      endDate: data?.end_date,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Update a budget limit
  async updateBudgetLimit(budgetId, updates) {
    const updateData = {};
    if (updates?.limitAmount !== undefined) updateData.limit_amount = updates?.limitAmount;
    if (updates?.alertThreshold1 !== undefined) updateData.alert_threshold_1 = updates?.alertThreshold1;
    if (updates?.alertThreshold2 !== undefined) updateData.alert_threshold_2 = updates?.alertThreshold2;
    if (updates?.rolloverEnabled !== undefined) updateData.rollover_enabled = updates?.rolloverEnabled;
    if (updates?.rolloverAmount !== undefined) updateData.rollover_amount = updates?.rolloverAmount;
    if (updates?.isRecurring !== undefined) updateData.is_recurring = updates?.isRecurring;
    if (updates?.recurrenceFrequency !== undefined) updateData.recurrence_frequency = updates?.recurrenceFrequency;
    if (updates?.endDate !== undefined) updateData.end_date = updates?.endDate;
    updateData.updated_at = new Date()?.toISOString();

    const { data, error } = await supabase?.from('budget_limits')?.update(updateData)?.eq('id', budgetId)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      userId: data?.user_id,
      categoryId: data?.category_id,
      monthYear: data?.month_year,
      limitAmount: parseFloat(data?.limit_amount),
      alertThreshold1: data?.alert_threshold_1,
      alertThreshold2: data?.alert_threshold_2,
      rolloverEnabled: data?.rollover_enabled,
      rolloverAmount: parseFloat(data?.rollover_amount),
      isRecurring: data?.is_recurring || false,
      recurrenceFrequency: data?.recurrence_frequency,
      startDate: data?.start_date,
      endDate: data?.end_date,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  // Delete a budget limit
  async deleteBudgetLimit(budgetId) {
    const { error } = await supabase?.from('budget_limits')?.delete()?.eq('id', budgetId);

    if (error) throw error;
  },

  // Get income estimate for a month
  async getIncomeEstimate(userId, monthYear) {
    try {
      const [year, month] = monthYear?.split('-');
      const startDate = `${year}-${month}-01`;
      const endDate = new Date(parseInt(year), parseInt(month), 0)?.toISOString()?.split('T')?.[0];

      const { data, error } = await supabase
        ?.from('transactions')
        ?.select('amount')
        ?.eq('user_id', userId)
        ?.eq('type', 'income')
        ?.gte('date', startDate)
        ?.lte('date', endDate);

      if (error) throw error;

      const totalIncome = data?.reduce((sum, t) => sum + Math.abs(parseFloat(t?.amount)), 0);
      return totalIncome;
    } catch (error) {
      console.error('Error fetching income estimate:', error);
      return 0;
    }
  },

  // Get active alerts for a user
  async getActiveAlerts(userId) {
    const { data, error } = await supabase?.from('budget_alerts')?.select(`
        *,
        budget_limits (
          id,
          category_id,
          month_year,
          limit_amount,
          categories (
            name,
            color,
            icon
          )
        )
      `)?.eq('user_id', userId)?.eq('status', 'active')?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(item => ({
      id: item?.id,
      userId: item?.user_id,
      budgetLimitId: item?.budget_limit_id,
      alertType: item?.alert_type,
      status: item?.status,
      percentageUsed: item?.percentage_used ? parseFloat(item?.percentage_used) : null,
      amountSpent: item?.amount_spent ? parseFloat(item?.amount_spent) : null,
      amountRemaining: item?.amount_remaining ? parseFloat(item?.amount_remaining) : null,
      message: item?.message,
      snoozedUntil: item?.snoozed_until,
      createdAt: item?.created_at,
      updatedAt: item?.updated_at,
      budgetLimit: item?.budget_limits ? {
        id: item?.budget_limits?.id,
        categoryId: item?.budget_limits?.category_id,
        monthYear: item?.budget_limits?.month_year,
        limitAmount: parseFloat(item?.budget_limits?.limit_amount),
        category: item?.budget_limits?.categories ? {
          name: item?.budget_limits?.categories?.name,
          color: item?.budget_limits?.categories?.color,
          icon: item?.budget_limits?.categories?.icon
        } : null
      } : null
    }));
  },

  // Update alert status
  async updateAlertStatus(alertId, status, snoozedUntil = null) {
    const updateData = {
      status,
      updated_at: new Date()?.toISOString()
    };
    if (snoozedUntil) updateData.snoozed_until = snoozedUntil;

    const { error } = await supabase?.from('budget_alerts')?.update(updateData)?.eq('id', alertId);

    if (error) throw error;
  },

  // Get notification history
  async getNotifications(userId, limit = 50) {
    const { data, error } = await supabase?.from('budget_notifications')?.select('*')?.eq('user_id', userId)?.order('sent_at', { ascending: false })?.limit(limit);

    if (error) throw error;

    return data?.map(item => ({
      id: item?.id,
      userId: item?.user_id,
      alertId: item?.alert_id,
      notificationMethod: item?.notification_method,
      title: item?.title,
      message: item?.message,
      read: item?.read,
      sentAt: item?.sent_at,
      createdAt: item?.created_at
    }));
  },

  // Mark notification as read
  async markNotificationRead(notificationId) {
    const { error } = await supabase?.from('budget_notifications')?.update({ read: true })?.eq('id', notificationId);

    if (error) throw error;
  },

  // Get all categories for budget creation
  async getCategories(userId) {
    const { data, error } = await supabase?.from('categories')?.select('*')?.eq('user_id', userId)?.eq('type', 'expense')?.order('name');

    if (error) throw error;

    return data?.map(item => ({
      id: item?.id,
      userId: item?.user_id,
      name: item?.name,
      type: item?.type,
      color: item?.color,
      icon: item?.icon,
      createdAt: item?.created_at
    }));
  }
};

export default budgetService;